import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Calendar, Plus, Trash2, Eye, EyeOff, ExternalLink, ArrowLeft, Users, Ticket,
  RotateCw, Star, MessageSquare,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { useToast } from "../hooks/use-toast";

const API = process.env.REACT_APP_BACKEND_URL;

export default function EventiAdminSection() {
  const [eventi, setEventi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [viewingIscrizioni, setViewingIscrizioni] = useState(null);
  const { toast } = useToast();

  const load = () => {
    setLoading(true);
    axios
      .get(`${API}/api/admin/eventi`, { withCredentials: true })
      .then((r) => setEventi(r.data || []))
      .catch(() => setEventi([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const togglePublish = async (e) => {
    const url = e.stato === "pubblicato" ? "unpublish" : "publish";
    try {
      await axios.post(`${API}/api/admin/eventi/${e.id}/${url}`, {}, { withCredentials: true });
      toast({ description: e.stato === "pubblicato" ? "Evento ritirato." : "Evento pubblicato." });
      load();
    } catch (err) {
      toast({ description: "Errore: " + (err.response?.data?.detail || err.message), variant: "destructive" });
    }
  };
  const handleDelete = async (e) => {
    if (!window.confirm(`Eliminare l'evento "${e.titolo}" e tutte le iscrizioni?`)) return;
    try {
      await axios.delete(`${API}/api/admin/eventi/${e.id}`, { withCredentials: true });
      toast({ description: "Evento eliminato." });
      load();
    } catch (err) {
      toast({ description: "Errore: " + (err.response?.data?.detail || err.message), variant: "destructive" });
    }
  };

  if (viewingIscrizioni) {
    return <IscrizioniView evento={viewingIscrizioni} onBack={() => setViewingIscrizioni(null)} />;
  }
  if (editing) {
    return (
      <EventoEditor
        evento={editing === "new" ? null : editing}
        onBack={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-extrabold text-white flex items-center gap-2">
            <Calendar className="h-6 w-6 text-[#f59e0b]" /> Eventi & Prenotazioni
          </h2>
          <p className="text-sm text-slate-400 mt-1">
            Gestisci webinar e workshop con iscrizione online e pagamento Stripe.
          </p>
        </div>
        <Button
          data-testid="eventi-admin-new-btn"
          onClick={() => setEditing("new")}
          className="bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1020] font-bold"
        >
          <Plus className="mr-2 h-4 w-4" /> Nuovo evento
        </Button>
      </div>

      {loading ? (
        <div className="text-slate-400 py-8 text-center">Caricamento…</div>
      ) : eventi.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/20 p-10 text-center text-slate-400">
          Nessun evento. Clicca &quot;Nuovo evento&quot; per iniziare.
        </div>
      ) : (
        <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden" data-testid="eventi-admin-list">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-slate-300 uppercase text-[10.5px] tracking-widest">
              <tr>
                <th className="px-4 py-3 text-left">Titolo</th>
                <th className="px-4 py-3 text-left">Data</th>
                <th className="px-4 py-3 text-left">Prezzo</th>
                <th className="px-4 py-3 text-left">Iscrizioni</th>
                <th className="px-4 py-3 text-left">Stato</th>
                <th className="px-4 py-3 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5 text-slate-200">
              {eventi.map((e) => (
                <tr key={e.id} className="hover:bg-white/5">
                  <td className="px-4 py-3 max-w-md">
                    <div className="font-bold text-white truncate">{e.titolo}</div>
                    <div className="text-[11px] text-slate-400 truncate">{e.luogo} · {e.modalita}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-300 text-[12.5px]">
                    {e.data_inizio ? new Date(e.data_inizio).toLocaleString("it-IT") : "—"}
                  </td>
                  <td className="px-4 py-3 text-slate-300">
                    {e.prezzo_eur > 0 ? `€ ${e.prezzo_eur.toFixed(2)}` : "Gratuito"}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => setViewingIscrizioni(e)}
                      data-testid={`eventi-admin-iscrizioni-${e.id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/5 hover:bg-white/15 text-white text-xs font-semibold"
                    >
                      <Users className="h-3 w-3" /> {e.posti_occupati || 0}
                      {e.capienza_max > 0 && `/${e.capienza_max}`}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    {e.stato === "pubblicato" ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-[10.5px] font-bold">
                        <Eye className="h-3 w-3" /> Pubblicato
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-500/20 text-slate-300 text-[10.5px] font-bold">
                        <EyeOff className="h-3 w-3" /> Bozza
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      {e.stato === "pubblicato" && (
                        <a
                          href={`/eventi/${e.slug}`}
                          target="_blank"
                          rel="noreferrer"
                          className="p-1.5 rounded-md hover:bg-white/10 text-slate-300"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      )}
                      <button
                        onClick={() => togglePublish(e)}
                        className="px-2 py-1 rounded-md bg-white/5 hover:bg-white/15 text-white text-xs font-semibold"
                        data-testid={`eventi-admin-toggle-${e.id}`}
                      >
                        {e.stato === "pubblicato" ? "Ritira" : "Pubblica"}
                      </button>
                      <button
                        onClick={() => setEditing(e)}
                        className="px-2 py-1 rounded-md bg-[#f59e0b]/90 hover:bg-[#f59e0b] text-[#0a1020] text-xs font-bold"
                        data-testid={`eventi-admin-edit-${e.id}`}
                      >
                        Modifica
                      </button>
                      <button
                        onClick={() => handleDelete(e)}
                        className="p-1.5 rounded-md hover:bg-red-500/20 text-red-400"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function EventoEditor({ evento, onBack, onSaved }) {
  const isNew = !evento;
  const [form, setForm] = useState({
    titolo: evento?.titolo || "",
    sottotitolo: evento?.sottotitolo || "",
    slug: evento?.slug || "",
    descrizione: evento?.descrizione || "",
    cover_url: evento?.cover_url || "",
    luogo: evento?.luogo || "Online",
    indirizzo: evento?.indirizzo || "",
    modalita: evento?.modalita || "online",
    data_inizio: evento?.data_inizio ? evento.data_inizio.slice(0, 16) : "",
    data_fine: evento?.data_fine ? evento.data_fine.slice(0, 16) : "",
    capienza_max: evento?.capienza_max ?? 0,
    prezzo_eur: evento?.prezzo_eur ?? 0,
    tags: (evento?.tags || []).join(", "),
    seo_title: evento?.seo_title || "",
    seo_description: evento?.seo_description || "",
  });
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async (andPublish = false) => {
    if (!form.titolo || !form.descrizione || !form.data_inizio) {
      toast({ description: "Titolo, descrizione e data inizio sono obbligatori.", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        capienza_max: parseInt(form.capienza_max || 0, 10),
        prezzo_eur: parseFloat(form.prezzo_eur || 0),
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        data_inizio: new Date(form.data_inizio).toISOString(),
        data_fine: form.data_fine ? new Date(form.data_fine).toISOString() : null,
      };
      if (isNew && !payload.slug) delete payload.slug;
      let saved;
      if (isNew) {
        const r = await axios.post(`${API}/api/admin/eventi`, payload, { withCredentials: true });
        saved = r.data;
      } else {
        const r = await axios.put(`${API}/api/admin/eventi/${evento.id}`, payload, { withCredentials: true });
        saved = r.data;
      }
      if (andPublish && saved?.id) {
        await axios.post(`${API}/api/admin/eventi/${saved.id}/publish`, {}, { withCredentials: true });
      }
      toast({ description: andPublish ? "Evento salvato e pubblicato." : "Evento salvato." });
      onSaved();
    } catch (e) {
      toast({ description: "Errore: " + (e.response?.data?.detail || e.message), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <button onClick={onBack} className="inline-flex items-center gap-1.5 text-slate-300 hover:text-white text-sm font-semibold">
          <ArrowLeft className="h-4 w-4" /> Torna alla lista
        </button>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => handleSave(false)} disabled={saving}>
            Salva bozza
          </Button>
          <Button onClick={() => handleSave(true)} disabled={saving} className="bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1020] font-bold">
            Salva e pubblica
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <Card title="Contenuto">
            <FormRow label="Titolo *"><Input value={form.titolo} onChange={(e) => set("titolo", e.target.value)} data-testid="evento-form-titolo" /></FormRow>
            <FormRow label="Sottotitolo"><Input value={form.sottotitolo} onChange={(e) => set("sottotitolo", e.target.value)} /></FormRow>
            <FormRow label="Descrizione *">
              <Textarea rows={10} value={form.descrizione} onChange={(e) => set("descrizione", e.target.value)} data-testid="evento-form-descrizione" />
            </FormRow>
            <FormRow label="Cover URL"><Input value={form.cover_url} onChange={(e) => set("cover_url", e.target.value)} /></FormRow>
          </Card>
        </div>
        <div className="space-y-4">
          <Card title="Data & luogo">
            <FormRow label="Data inizio *">
              <Input type="datetime-local" value={form.data_inizio} onChange={(e) => set("data_inizio", e.target.value)} data-testid="evento-form-data-inizio" />
            </FormRow>
            <FormRow label="Data fine">
              <Input type="datetime-local" value={form.data_fine} onChange={(e) => set("data_fine", e.target.value)} />
            </FormRow>
            <FormRow label="Modalità">
              <select
                value={form.modalita}
                onChange={(e) => set("modalita", e.target.value)}
                className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900"
                data-testid="evento-form-modalita"
              >
                <option value="online">Online</option>
                <option value="presenza">In presenza</option>
                <option value="ibrido">Ibrido</option>
              </select>
            </FormRow>
            <FormRow label="Luogo"><Input value={form.luogo} onChange={(e) => set("luogo", e.target.value)} /></FormRow>
            <FormRow label="Indirizzo"><Input value={form.indirizzo} onChange={(e) => set("indirizzo", e.target.value)} /></FormRow>
          </Card>
          <Card title="Prezzo & capienza">
            <FormRow label="Prezzo € (0 = gratis)">
              <Input type="number" step="0.01" min="0" value={form.prezzo_eur} onChange={(e) => set("prezzo_eur", e.target.value)} data-testid="evento-form-prezzo" />
            </FormRow>
            <FormRow label="Capienza max (0 = illimitata)">
              <Input type="number" min="0" value={form.capienza_max} onChange={(e) => set("capienza_max", e.target.value)} data-testid="evento-form-capienza" />
            </FormRow>
            <FormRow label="Tag (virgola)"><Input value={form.tags} onChange={(e) => set("tags", e.target.value)} /></FormRow>
            <FormRow label="Slug (opz.)"><Input value={form.slug} onChange={(e) => set("slug", e.target.value)} /></FormRow>
          </Card>
          <Card title="SEO">
            <FormRow label="SEO title"><Input value={form.seo_title} onChange={(e) => set("seo_title", e.target.value)} /></FormRow>
            <FormRow label="Meta description"><Textarea rows={3} value={form.seo_description} onChange={(e) => set("seo_description", e.target.value)} /></FormRow>
          </Card>
        </div>
      </div>
    </div>
  );
}

function IscrizioniView({ evento, onBack }) {
  const [iscrizioni, setIscrizioni] = useState([]);
  const [feedback, setFeedback] = useState({ items: [], media: 0, totale: 0, distrib: { "1": 0, "2": 0, "3": 0 } });
  const [loading, setLoading] = useState(true);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [resettingId, setResettingId] = useState(null);
  const { toast } = useToast();
  const load = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/api/admin/eventi/${evento.id}/iscrizioni`, { withCredentials: true }),
      axios.get(`${API}/api/admin/eventi/${evento.id}/feedback`, { withCredentials: true }),
    ])
      .then(([iscR, fbR]) => {
        setIscrizioni(iscR.data || []);
        setFeedback(fbR.data || { items: [], media: 0, totale: 0, distrib: {} });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(load, [evento.id]);

  const handleReminderNow = async () => {
    if (!window.confirm("Inviare subito il reminder 24h a tutti gli iscritti che non lo hanno ancora ricevuto?")) return;
    setSendingReminder(true);
    try {
      const r = await axios.post(
        `${API}/api/admin/eventi/${evento.id}/reminder-now`,
        {},
        { withCredentials: true }
      );
      toast({ description: `Reminder inviati: ${r.data.inviati} · Falliti: ${r.data.falliti}` });
      load();
    } catch (e) {
      toast({ description: "Errore: " + (e.response?.data?.detail || e.message), variant: "destructive" });
    } finally {
      setSendingReminder(false);
    }
  };

  const handleResetReminder = async (isc) => {
    if (!window.confirm(`Rispedire il reminder 24h a ${isc.nome} ${isc.cognome} (${isc.email})? Verrà inviato anche se già ricevuto.`)) return;
    setResettingId(isc.id);
    try {
      const r = await axios.post(
        `${API}/api/admin/eventi/${evento.id}/iscrizioni/${isc.id}/reset-reminder?resend=true`,
        {},
        { withCredentials: true }
      );
      toast({
        description: r.data.resent
          ? "Reminder reinviato con successo."
          : `Reset ok ma reinvio non riuscito: ${r.data.reason || "verifica configurazione email"}`,
        variant: r.data.resent ? "default" : "destructive",
      });
      load();
    } catch (e) {
      toast({ description: "Errore: " + (e.response?.data?.detail || e.message), variant: "destructive" });
    } finally {
      setResettingId(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <button onClick={onBack} className="inline-flex items-center gap-1.5 text-slate-300 hover:text-white text-sm font-semibold">
          <ArrowLeft className="h-4 w-4" /> Torna agli eventi
        </button>
        <div className="flex items-center gap-3">
          <Button
            onClick={handleReminderNow}
            disabled={sendingReminder || iscrizioni.length === 0}
            data-testid="evento-reminder-now"
            className="bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1020] font-bold"
          >
            {sendingReminder ? "Invio…" : "Invia reminder ora"}
          </Button>
          <div className="inline-flex items-center gap-2 text-white font-bold">
            <Ticket className="h-4 w-4 text-[#f59e0b]" />
            {iscrizioni.length} iscrizioni · {evento.titolo}
          </div>
        </div>
      </div>

      {/* Feedback stats */}
      {feedback.totale > 0 && (
        <div className="mb-6 rounded-2xl bg-gradient-to-br from-[#f59e0b]/15 to-[#f59e0b]/5 border border-[#f59e0b]/30 p-5" data-testid="feedback-stats-card">
          <div className="flex flex-wrap items-center gap-6">
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-[#fbbf24]">Feedback ricevuti</div>
              <div className="mt-1 text-3xl font-extrabold text-white">
                {feedback.media} <span className="text-lg text-slate-300 font-normal">/ 3</span>
              </div>
              <div className="text-[11px] text-slate-400">{feedback.totale} risposte</div>
            </div>
            <div className="flex items-center gap-1">
              {[1, 2, 3].map((n) => (
                <Star
                  key={n}
                  className={`h-8 w-8 ${
                    feedback.media >= n ? "fill-[#f59e0b] text-[#f59e0b]" : "text-slate-600"
                  }`}
                />
              ))}
            </div>
            <div className="flex-1 min-w-[200px] space-y-1">
              {[3, 2, 1].map((n) => {
                const count = feedback.distrib?.[String(n)] || 0;
                const pct = feedback.totale > 0 ? (count / feedback.totale) * 100 : 0;
                return (
                  <div key={n} className="flex items-center gap-2 text-xs">
                    <span className="w-3 text-slate-400 font-bold">{n}</span>
                    <div className="flex-1 h-2 rounded-full bg-white/10 overflow-hidden">
                      <div className="h-full bg-[#f59e0b]" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="w-8 text-right text-slate-300">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
          {feedback.items.filter((i) => (i.commento || "").trim()).length > 0 && (
            <details className="mt-5">
              <summary className="cursor-pointer text-xs font-bold text-[#fbbf24] hover:text-[#f59e0b] inline-flex items-center gap-1">
                <MessageSquare className="h-3.5 w-3.5" /> Vedi commenti ({feedback.items.filter((i) => (i.commento || "").trim()).length})
              </summary>
              <div className="mt-3 space-y-2 max-h-64 overflow-y-auto pr-2" data-testid="feedback-commenti-list">
                {feedback.items
                  .filter((i) => (i.commento || "").trim())
                  .map((i) => (
                    <div key={i.id} className="p-3 rounded-lg bg-white/5 border border-white/10 text-[13px]">
                      <div className="flex items-center gap-2 mb-1.5">
                        <div className="flex gap-0.5">
                          {[1, 2, 3].map((n) => (
                            <Star key={n} className={`h-3 w-3 ${i.rating >= n ? "fill-[#f59e0b] text-[#f59e0b]" : "text-slate-600"}`} />
                          ))}
                        </div>
                        <span className="text-slate-500 text-[10.5px]">— {i.nome}</span>
                      </div>
                      <div className="text-slate-200 leading-relaxed">&ldquo;{i.commento}&rdquo;</div>
                    </div>
                  ))}
              </div>
            </details>
          )}
        </div>
      )}
      {loading ? (
        <div className="text-slate-400 py-8 text-center">Caricamento…</div>
      ) : iscrizioni.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/20 p-10 text-center text-slate-400">
          Nessuna iscrizione ancora.
        </div>
      ) : (
        <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-slate-300 uppercase text-[10.5px] tracking-widest">
              <tr>
                <th className="px-4 py-3 text-left">Nome</th>
                <th className="px-4 py-3 text-left">Email</th>
                <th className="px-4 py-3 text-left">Importo</th>
                <th className="px-4 py-3 text-left">Stato</th>
                <th className="px-4 py-3 text-left">Reminder</th>
                <th className="px-4 py-3 text-left">Follow-up</th>
                <th className="px-4 py-3 text-left">Data</th>
                <th className="px-4 py-3 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5 text-slate-200">
              {iscrizioni.map((i) => (
                <tr key={i.id}>
                  <td className="px-4 py-3 font-bold text-white">{i.nome} {i.cognome}</td>
                  <td className="px-4 py-3">{i.email}</td>
                  <td className="px-4 py-3">{i.amount > 0 ? `€ ${i.amount.toFixed(2)}` : "Gratis"}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-[10.5px] font-bold ${
                      i.stato === "confermata" || i.stato === "gratuita"
                        ? "bg-emerald-500/20 text-emerald-300"
                        : i.stato === "pending"
                        ? "bg-yellow-500/20 text-yellow-300"
                        : "bg-red-500/20 text-red-300"
                    }`}>
                      {i.stato}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {i.reminder_24h_inviato_at ? (
                      <span className="text-emerald-400 text-[11px]">Inviato</span>
                    ) : (
                      <span className="text-slate-500 text-[11px]">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[11px]">
                    <span className={i.followup_step_1_inviato_at ? "text-emerald-400 font-bold" : "text-slate-400"}>1</span>
                    <span className="mx-0.5 text-slate-500">·</span>
                    <span className={i.followup_step_2_inviato_at ? "text-emerald-400 font-bold" : "text-slate-400"}>2</span>
                    <span className="mx-0.5 text-slate-500">·</span>
                    <span className={i.followup_step_3_inviato_at ? "text-emerald-400 font-bold" : "text-slate-400"}>3</span>
                  </td>
                  <td className="px-4 py-3 text-slate-400 text-[12px]">
                    {new Date(i.created_at).toLocaleString("it-IT")}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => handleResetReminder(i)}
                      disabled={resettingId === i.id || !(i.stato === "confermata" || i.stato === "gratuita")}
                      data-testid={`reset-reminder-${i.id}`}
                      title="Rispedisci reminder anche se già ricevuto"
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/5 hover:bg-white/15 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-semibold"
                    >
                      <RotateCw className={`h-3 w-3 ${resettingId === i.id ? "animate-spin" : ""}`} />
                      Reset & Reinvia
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
      <div className="text-xs font-extrabold uppercase tracking-widest text-[#f59e0b] mb-4">{title}</div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}
function FormRow({ label, children }) {
  return (
    <div>
      <Label className="text-slate-200 text-xs font-bold uppercase tracking-wider">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
